import { SidebarLink } from "./SidebarLink";
import { SidebarModule, SidebarModuleFlyout } from "./SidebarModule";
import { isActiveItem, isModule } from "./navModel";
import type { NavGroup } from "./navTypes";
import { type OpenGroupsControl } from "./useOpenGroups";

interface SidebarNavProps {
  nav: NavGroup[];
  active?: string;
  collapsed: boolean;
  openGroups: OpenGroupsControl;
  onNavigate?: (id: string) => void;
}

/** The main menu: expandable modules and plain groups of links. */
export function SidebarNav({ nav, active, collapsed, openGroups, onNavigate }: SidebarNavProps): React.JSX.Element {
  return (
    <nav className="nb-side-nav" aria-label="Main menu">
      {nav.map((group) => {
        if (!isModule(group)) {
          return <SidebarPlainGroup key={group.id} group={group} active={active} collapsed={collapsed} onNavigate={onNavigate} />;
        }
        if (collapsed) {
          return <SidebarModuleFlyout key={group.id} group={group} active={active} onNavigate={onNavigate} />;
        }
        return (
          <SidebarModule
            key={group.id}
            group={group}
            active={active}
            open={openGroups.isOpen(group.id)}
            onToggle={(open) => openGroups.setOpen(group.id, open)}
            onNavigate={onNavigate}
          />
        );
      })}
    </nav>
  );
}

function SidebarPlainGroup({ group, active, collapsed, onNavigate }: Omit<SidebarNavProps, "nav" | "openGroups"> & { group: NavGroup }): React.JSX.Element {
  return (
    <div role="group" aria-label={group.label} className="nb-side-plain">
      {group.label ? (
        <div className="nb-side-group" aria-hidden="true">
          {group.label}
        </div>
      ) : null}
      {group.items.map((item) => (
        <SidebarLink key={item.id} item={item} active={isActiveItem(item, active)} collapsed={collapsed} onNavigate={onNavigate} />
      ))}
    </div>
  );
}
